import {
  CreateAchOrderErrorType,
  CreateBankInternalTransferErrorType,
  CreateBankInvestmentTransferErrorType,
  CreateInvestmentAchTransferErrorType,
} from '@egw/api'
import { Button, ButtonVariant, ModalDrawer } from '@egw/components'
import { Trans, useTranslation } from '@egw/i18n'
import { type FC, type ReactNode } from 'react'
import { useTransferContextProvider } from '../../context'
import { BankInvestmentTransferValidationError, EBackendError } from '../../types'
import { AccountLiquidation } from '../account-liquidation/account-liquidation'
import { ClientServicesLink } from '../client-services-link/client-services-link'

export const TransferErrorModal: FC = () => {
  const {
    bankInvestmentError,
    achError,
    internalBankTransferError,
    bankInvestmentValidationError,
    investmentAchError,
    clearTransferErrors,
  } = useTransferContextProvider()

  const { t } = useTranslation('features-transfer')

  return (
    <ModalDrawer
      onClose={clearTransferErrors}
      isOpen={Boolean(achError ?? bankInvestmentError ?? internalBankTransferError ?? investmentAchError)}
      title={t('transfer.unable_to_transfer')}
    >
      <div id="transfer-rejected" className="w-full">
        {bankInvestmentError ? <>{getBankInvestmentErrorModal(bankInvestmentError)}</> : null}
        {achError ? <>{getAchTransferErrorModal(achError)}</> : null}
        {internalBankTransferError ? <>{getInternalBankErrorModal(internalBankTransferError)}</> : null}
        {/**
         * bankInvestmentValidationError is used to show the blue alert messages, no modal will be shown (isOpen should not have bankInvestmentValidationError)
         * Below will be kept as a placeholder in case of any changes.
         */}
        {bankInvestmentValidationError !== BankInvestmentTransferValidationError.NA ? (
          <>{getBankInvestmentTransferValidationModal(bankInvestmentValidationError)}</>
        ) : null}
        {investmentAchError ? <>{getInvestmentAchErrorModal(investmentAchError)}</> : null}
      </div>
    </ModalDrawer>
  )
}

export const ContactClientService: FC<{
  errorType:
    | CreateAchOrderErrorType
    | EBackendError
    | CreateBankInvestmentTransferErrorType
    | BankInvestmentTransferValidationError
    | CreateInvestmentAchTransferErrorType
}> = ({ errorType }) => {
  const { t } = useTranslation('features-transfer')
  const { clearTransferErrors } = useTransferContextProvider()
  return (
    <p className="w-full flex flex-col gap-4">
      <Trans t={t} i18nKey={`transfer.error.${errorType}`}>
        <ClientServicesLink onClickCb={clearTransferErrors} />
      </Trans>
    </p>
  )
}

export const EgwBankGenericError: FC<{ bankInvestmentError?: boolean }> = ({ bankInvestmentError }) => {
  const { clearTransferErrors } = useTransferContextProvider()
  const { t } = useTranslation('features-transfer')

  return (
    <div id="transfer-rejected" className="w-full gap-2 flex flex-col">
      <p className="text-alert-500">{t('transfer.error_occurred')}</p>
      <p>{t('transfer.please_ensure')}</p>
      <ul className="flex flex-col list-disc list-outside ml-5 gap-4">
        <li>{t('transfer.bank_investment_generic_error_li1')}</li>
        {bankInvestmentError ? <li>{t('transfer.bank_investment_generic_error_li2')}</li> : null}
      </ul>
      <Button variant={ButtonVariant.Primary} id="tryAgain" className="mt-5" onClick={clearTransferErrors}>
        {t('transfer.try_again')}
      </Button>
    </div>
  )
}

export const BankInvestmentTransferError: FC<{ errorType: CreateBankInvestmentTransferErrorType | EBackendError }> = ({
  errorType,
}) => {
  const { t } = useTranslation('features-transfer')

  return <p>{t(`transfer.bank_investment_transfer_error.${errorType}`)}</p>
}

function getAchTransferErrorModal(
  errorType: CreateAchOrderErrorType | EBackendError | 'BankingMinimumDepositNotMetError',
): ReactNode {
  switch (errorType) {
    case EBackendError.GENERIC:
    case EBackendError.NSF:
    case EBackendError.VelocityLimitExceeded:
    case CreateAchOrderErrorType.LINKED_ACH_ACCOUNT_NOT_ACTIVE:
    case CreateAchOrderErrorType.UNABLE_TO_CREATE_ACH_ORDER:
      // case CreateAchOrderErrorType.BadRequest: @deprecated
      // case CreateAchOrderErrorType.NotAvailableForAccount: @deprecated
      return <ContactClientService errorType={errorType} />

    // ERRORS BELOW SHOW INLINE ERRORS NO NEED TO SHOW HERE
    // case CreateAchOrderErrorType.InadequateDepositAmount: @deprecated
    case CreateAchOrderErrorType.INSUFFICIENT_FUNDS:
    case 'BankingMinimumDepositNotMetError':
      return null
  }
}

function getBankInvestmentErrorModal(errorType: CreateBankInvestmentTransferErrorType | EBackendError): ReactNode {
  switch (errorType) {
    // BACKEND GENERIC ERRORS
    case EBackendError.GENERIC:
    case EBackendError.NSF:
    case EBackendError.VelocityLimitExceeded:
      return <ContactClientService errorType={errorType} />

    // SPECIFIC ERROR TYPES
    case CreateBankInvestmentTransferErrorType.PENDING_TRANSFER_ALREADY_EXISTS:
      return <BankInvestmentTransferError errorType={errorType} />

    case CreateBankInvestmentTransferErrorType.AMOUNT_REQUESTED_REQUIRES_LIQUIDATION:
      return <AccountLiquidation />

    // BANK INVESTMENT GENERIC ERRORS
    case CreateBankInvestmentTransferErrorType.BAD_REQUEST:
      return <EgwBankGenericError bankInvestmentError />

    // THESE ERROR TYPES ARE HANDED AS INLINE ERRORS HENCE SHOWING NULL
    case CreateBankInvestmentTransferErrorType.INSUFFICIENT_FUNDS:
    case CreateBankInvestmentTransferErrorType.MINIMUM_DEPOSIT_NOT_MET:
      return null
  }
}

function getInvestmentAchErrorModal(
  errorType:
    | CreateInvestmentAchTransferErrorType
    | 'AnnualRetirementContributionExceededError'
    | EBackendError
    | undefined,
): ReactNode {
  switch (errorType) {
    case EBackendError.GENERIC:
    case EBackendError.NSF:
    case EBackendError.VelocityLimitExceeded:
    case CreateInvestmentAchTransferErrorType.LINKED_ACCOUNT_ERROR:
    case CreateInvestmentAchTransferErrorType.TRANSFER_FAILED:
    case CreateInvestmentAchTransferErrorType.TRANSFER_LIMIT_BREACHED:
    case CreateInvestmentAchTransferErrorType.AMOUNT_REQUESTED_REQUIRES_LIQUIDATION:
      return <ContactClientService errorType={errorType} />

    case CreateInvestmentAchTransferErrorType.BAD_REQUEST:
      return <EgwBankGenericError />

    // THESE ERROR TYPES ARE HANDLED AS INLINE ERRORS HENCE SHOWING NULL
    case CreateInvestmentAchTransferErrorType.INSUFFICIENT_FUNDS:
    case 'AnnualRetirementContributionExceededError':
      return null
  }
}

function getInternalBankErrorModal(errorType: CreateBankInternalTransferErrorType | EBackendError): ReactNode {
  switch (errorType) {
    case EBackendError.GENERIC:
    case EBackendError.NSF:
    case EBackendError.VelocityLimitExceeded:
      return <ContactClientService errorType={errorType} />

    case CreateBankInternalTransferErrorType.BAD_REQUEST:
    case CreateBankInternalTransferErrorType.FAILED_TO_CREATE_TRANSFER:
      return <EgwBankGenericError />

    // THESE ERROR TYPES ARE HANDED AS INLINE ERRORS HENCE SHOWING NULL
    case CreateBankInternalTransferErrorType.INSUFFICIENT_FUNDS:
      return null
  }
}

function getBankInvestmentTransferValidationModal(errorType: BankInvestmentTransferValidationError): ReactNode {
  switch (errorType) {
    case BankInvestmentTransferValidationError.BAD_REQUEST: // NOT GOING TO SHOW ANYTHING FOR THIS ERROR TYPE && ALLOW USER TO CONTINUE AND GET FLAGGED BY CreateBankInvestmentTransferErrorType.BAD_REQUEST
    case BankInvestmentTransferValidationError.DEPOSIT: // THESE ERROR TYPES ARE HANDLED AS BLUE ALERT MESSAGES
    case BankInvestmentTransferValidationError.WITHDRAWAL: // THESE ERROR TYPES ARE HANDLED AS BLUE ALERT MESSAGES
    case BankInvestmentTransferValidationError.NA: // NO ERRORS TO SHOW
      return null
  }
}
